import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import ProgressMeter from "../components/ProgressMeter";
import PageHeader from "../components/PageHeader";
import ResultSection from "../components/ResultSection";
import SectionExpandToolbar from "../components/SectionExpandToolbar";
import { RK, ReportLabel, ReportList, ReportRichList } from "../components/reports/ReportKit";
import { api, formatApiErrorDetail } from "../lib/api";
import jsPDF from "jspdf";
import {
  Target, ArrowLeft, Loader, Send, Trash2, ChevronRight, AlertTriangle, Download, Mic,
} from "lucide-react";

const LEVELS = ["Entry", "Mid", "Senior", "Staff", "Principal", "Director", "VP"];

function InterviewWizard() {
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [level, setLevel] = useState("Senior");
  const [focusInput, setFocusInput] = useState("");
  const [useLastAnalysis, setUseLastAnalysis] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!role.trim()) {
      setError("Enter a role title.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const focus = focusInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 6);
      const { data } = await api.post("/interview-questions", {
        role: role.trim(),
        level,
        focus,
        use_last_analysis: useLastAnalysis,
      });
      // Poll the job
      const jobId = data.job_id;
      for (let i = 0; i < 60; i += 1) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const r = await api.get(`/jobs/${jobId}`);
          if (r.data.status === "complete" && r.data.bank_id) {
            navigate(`/interview-questions/${r.data.bank_id}`);
            return;
          }
          if (r.data.status === "error") {
            throw new Error(r.data.error || "Generation failed");
          }
        } catch (e) {
          if (e?.response?.status === 404) continue;
          throw e;
        }
      }
      throw new Error("Timed out waiting for question bank. Try again.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <button onClick={() => navigate("/resources")} className="rm-tag mb-3" data-testid="back-to-resources-btn">
        <ArrowLeft size={14} /> Field guide
      </button>
      <div className="rm-tag mb-2">Interview prep</div>
      <h1 className="font-display text-4xl md:text-5xl uppercase mb-2">
        Generate an <span className="rm-text-orange">interview bank.</span>
      </h1>
      <p className="text-base md:text-lg max-w-2xl mb-8" style={{ color: "#475569" }}>
        Pick a role and a level. Resmo will return 20–26 questions across 4–5 categories,
        plus trap questions and smart questions to ask the interviewer.
      </p>

      <div className="rm-card-static p-6 md:p-8 max-w-2xl">
        <div className="space-y-4">
          <div>
            <div className="rm-label mb-2">Role</div>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="rm-input w-full"
              placeholder="e.g. Staff Backend Engineer, VP of Product, Senior Data Scientist"
              data-testid="interview-role-input"
            />
          </div>
          <div>
            <div className="rm-label mb-2">Experience level</div>
            <div className="flex flex-wrap gap-2" data-testid="interview-level-pills">
              {LEVELS.map((l) => (
                <button
                  key={l}
                  onClick={() => setLevel(l)}
                  className={`rm-tag ${level === l ? "bg-yellow-300" : ""}`}
                  type="button"
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="rm-label mb-2">Focus areas (optional, comma-separated)</div>
            <input
              type="text"
              value={focusInput}
              onChange={(e) => setFocusInput(e.target.value)}
              className="rm-input w-full"
              placeholder="distributed systems, mentoring, system design"
              data-testid="interview-focus-input"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useLastAnalysis}
              onChange={(e) => setUseLastAnalysis(e.target.checked)}
              data-testid="interview-use-last-analysis"
            />
            <span className="text-sm">Personalize using my last tailored analysis (resume + job description context)</span>
          </label>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}>
            <AlertTriangle size={14} className="inline mr-1" /> {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting || !role.trim()}
          className="rm-btn rm-btn-primary mt-6"
          data-testid="interview-submit-btn"
        >
          {submitting ? <><Loader size={16} className="animate-spin" /> Generating…</> : <><Send size={16} /> Generate bank</>}
        </button>
        {submitting && (
          <div className="mt-3 max-w-sm">
            <ProgressMeter expectedSeconds={35} label="Generating your question bank" testid="interview-progress-meter" />
          </div>
        )}
      </div>
    </div>
  );
}


function InterviewResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState({}); // category index -> bool

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/interview-questions/${id}`);
        setData(data);
        // Open first category by default
        setOpen({ 0: true });
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Delete this question bank?")) return;
    try {
      await api.delete(`/interview-questions/${id}`);
      navigate("/resources");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleDownloadPdf = () => {
    if (!data) return;
    const r = data.result || {};
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 50;
    const maxWidth = pageWidth - margin * 2;
    let y = margin;

    const ensureRoom = (need = 18) => {
      if (y + need > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const writeLine = (text, opts = {}) => {
      const fontSize = opts.size || 11;
      const bold = opts.bold || false;
      const color = opts.color || [15, 27, 45];
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(text || ""), maxWidth - (opts.indent || 0));
      lines.forEach((ln) => {
        ensureRoom(fontSize * 1.3);
        doc.text(ln, margin + (opts.indent || 0), y);
        y += fontSize * 1.3;
      });
    };

    const sectionRule = () => {
      ensureRoom(8);
      doc.setDrawColor(255, 106, 0);
      doc.setLineWidth(2);
      doc.line(margin, y, pageWidth - margin, y);
      y += 12;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
    };

    // Header
    writeLine("INTERVIEW BANK", { size: 9, bold: true, color: [255, 106, 0] });
    writeLine(data.role || "Untitled", { size: 22, bold: true });
    const subline = [data.level, ...(data.focus || [])].filter(Boolean).join(" · ");
    if (subline) writeLine(subline, { size: 10, color: [100, 116, 139] });
    y += 6;
    sectionRule();

    if (r.opener) {
      writeLine("WHAT TO EXPECT", { size: 11, bold: true, color: [255, 106, 0] });
      writeLine(r.opener);
      y += 8;
    }

    (r.categories || []).forEach((c, ci) => {
      ensureRoom(40);
      writeLine(
        `${String(ci + 1).padStart(2, "0")}  ${(c.name || "").toUpperCase()}`,
        { size: 14, bold: true }
      );
      if (c.description) writeLine(c.description, { size: 10, color: [100, 116, 139] });
      y += 4;
      (c.questions || []).forEach((q, qi) => {
        ensureRoom(40);
        writeLine(`Q${qi + 1}. ${q.q || ""}`, { bold: true, size: 11 });
        if (q.why) writeLine(`PROBING: ${q.why}`, { size: 10, color: [100, 116, 139], indent: 12 });
        if (q.answer_framework) {
          writeLine(`HOW TO ANSWER: ${q.answer_framework}`, { size: 10, color: [6, 95, 70], indent: 12 });
        }
        y += 6;
      });
      y += 6;
    });

    if ((r.red_flag_questions || []).length) {
      sectionRule();
      writeLine("TRAP QUESTIONS — WATCH OUT", { size: 12, bold: true, color: [153, 27, 27] });
      r.red_flag_questions.forEach((q) => {
        ensureRoom(30);
        writeLine(`! ${q.q || ""}`, { bold: true });
        if (q.trap) writeLine(q.trap, { size: 10, color: [100, 116, 139], indent: 12 });
        y += 4;
      });
      y += 6;
    }

    if ((r.closing_questions_to_ask || []).length) {
      sectionRule();
      writeLine("QUESTIONS TO ASK BACK", { size: 12, bold: true, color: [6, 95, 70] });
      r.closing_questions_to_ask.forEach((q, i) => {
        ensureRoom(16);
        writeLine(`${String(i + 1).padStart(2, "0")}. ${q}`);
      });
    }

    // Footer
    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i += 1) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated by Resmo · resmo.app · Page ${i} of ${total}`,
        pageWidth / 2,
        pageHeight - 20,
        { align: "center" }
      );
    }

    const filename = `interview-bank-${(data.role || "untitled").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
    doc.save(filename);
  };

  if (error) {
    return (
      <div>
        <button onClick={() => navigate("/resources")} className="rm-tag mb-3">
          <ArrowLeft size={14} /> Field guide
        </button>
        <div className="rm-card-static p-6" style={{ background: "#FEF2F2", borderColor: "#EF4444" }}>
          <div className="font-bold">Couldn't load this bank</div>
          <div className="font-mono-rm text-sm mt-2">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) return <div className="font-mono-rm text-center py-16">Loading…</div>;

  const r = data.result || {};
  const categories = r.categories || [];

  return (
    <div>
      <button onClick={() => navigate("/resources")} className="rm-tag mb-3" data-testid="back-to-resources-btn">
        <ArrowLeft size={14} /> Field guide
      </button>

      <PageHeader
        testid="interview-result-header"
        eyebrow="Interview bank"
        title={data.role}
        subtitle={[data.level, ...(data.focus || [])].filter(Boolean).join(" · ")}
        meta={[
          { label: "Categories", value: String(categories.length), tone: "neutral" },
          { label: "Total Qs", value: String(categories.reduce((acc, c) => acc + (c.questions?.length || 0), 0)), tone: "neutral" },
          { label: "Traps", value: String((r.red_flag_questions || []).length), tone: (r.red_flag_questions || []).length > 0 ? "danger" : "neutral" },
        ]}
        actions={
          <>
            <button
              onClick={() => navigate(`/mock-interview?bank_id=${data.id}`)}
              className="rm-btn rm-btn-primary text-xs px-3 py-2"
              style={{ background: "#0F1B2D", color: "#20C1A7", borderColor: "#20C1A7",
                       boxShadow: "3px 3px 0 0 #20C1A7" }}
              data-testid="run-mock-from-bank-btn"
              title="Practice this bank as a live mock interview"
            >
              <Mic size={14} /> Run mock →
            </button>
            <button
              onClick={handleDownloadPdf}
              className="rm-btn rm-btn-primary text-xs px-3 py-2"
              data-testid="download-interview-pdf-btn"
            >
              <Download size={14} /> PDF
            </button>
            <Link to="/resources/interview/new" className="rm-btn rm-btn-secondary text-xs px-3 py-2">
              New bank
            </Link>
            <button onClick={handleDelete} className="rm-btn rm-btn-ghost text-xs px-3 py-2" data-testid="delete-bank-btn">
              <Trash2 size={14} />
            </button>
          </>
        }
      />

      <SectionExpandToolbar
        group="interview-result"
        targetSelector='[data-testid="interview-sections"]'
        pdfTitle={`Interview bank · ${data.role || "Untitled role"}`}
        pdfFilename={`resmo-interview-${(data.role || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`}
      />
      <div className="space-y-3" data-testid="interview-sections">
        <ResultSection
          title="Overview"
          eyebrow="What to expect + category map"
          accentGradient="linear-gradient(135deg,#FBBF24 0%,#CA8A04 100%)"
          defaultOpen
          group="interview-result"
          testid="interview-section-overview"
        >
          <div data-testid="interview-overview">
            {r.opener && (
              <div className="rm-card-static p-6 mb-4" style={{ background: "#0F1B2D", color: "white" }}>
                <div className="rm-label mb-2" style={{ color: "#FFC145" }}>What to expect</div>
                <p className="text-sm md:text-base">{r.opener}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {categories.map((c, i) => (
                <div key={i} className="p-4"
                     style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8 }}
                     data-testid={`interview-category-summary-${i}`}>
                  <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                       style={{ color: "#B91C1C", fontWeight: 700 }}>
                    Category {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display text-base uppercase mt-0.5" style={{ color: "#0F1B2D" }}>
                    {c.name}
                  </div>
                  <div className="text-[12px] mt-1.5" style={{ color: "#475569" }}>
                    {c.description}
                  </div>
                  <div className="text-[11px] mt-2 font-semibold" style={{ color: "#64748B" }}>
                    {c.questions?.length || 0} questions
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ResultSection>

        <ResultSection
          title="Questions"
          eyebrow="Categorised question bank"
          count={categories.reduce((a, c) => a + (c.questions?.length || 0), 0)}
          accentGradient="linear-gradient(135deg,#F97316 0%,#B91C1C 100%)"
          group="interview-result"
          testid="interview-section-questions"
        >
          <div className="space-y-3" data-testid="interview-categories">
            {categories.map((c, i) => {
              const isOpen = !!open[i];
              return (
                <div key={i} className="rm-card-static overflow-hidden" data-testid={`category-card-${i}`}>
                  <button
                    className="w-full p-4 md:p-5 text-left flex items-start gap-3"
                    onClick={() => setOpen((s) => ({ ...s, [i]: !s[i] }))}
                  >
                    <div
                      className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 font-display"
                      style={{ background: "#0F1B2D", color: "white" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg md:text-xl uppercase leading-tight">{c.name}</div>
                      <div className="text-sm mt-1" style={{ color: "#475569" }}>{c.description}</div>
                    </div>
                    <ChevronRight
                      size={20}
                      className="flex-shrink-0 mt-1 transition-transform"
                      style={{ transform: isOpen ? "rotate(90deg)" : "none" }}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-4 md:px-5 pb-5 space-y-3">
                      {(c.questions || []).map((q, qi) => (
                        <div
                          key={qi}
                          className="p-3.5 pl-7 rounded-md relative"
                          style={{ border: "1.5px solid #CBD5E1", background: "white" }}
                          data-testid={`question-${i}-${qi}`}
                        >
                          <span style={{ position: "absolute", left: 14, top: 21, width: 5, height: 5, borderRadius: "50%", background: RK.orange }} />
                          <div className="font-semibold text-[13.5px] leading-snug" style={{ color: RK.navy }}>{q.q}</div>
                          {q.why && (
                            <div className="text-[12.5px] mt-1.5 leading-snug" style={{ color: RK.sub }}>
                              <ReportLabel color={RK.slate} className="mr-1">Probing</ReportLabel>
                              {q.why}
                            </div>
                          )}
                          {q.answer_framework && (
                            <div className="text-[12.5px] mt-1.5 leading-snug" style={{ color: RK.body }}>
                              <ReportLabel color="#065F46" className="mr-1">How to answer</ReportLabel>
                              {q.answer_framework}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ResultSection>

        {(r.red_flag_questions || []).length > 0 && (
          <ResultSection
            title="Traps"
            eyebrow="Watch out for these"
            count={(r.red_flag_questions || []).length}
            accentGradient="linear-gradient(135deg,#EF4444 0%,#991B1B 100%)"
            group="interview-result"
            testid="interview-section-traps"
          >
            <ReportRichList
              icon={<AlertTriangle size={11} />}
              title="Red flags — watch out"
              color={RK.red}
              items={r.red_flag_questions.map((q) => ({ heading: q.q, body: q.trap }))}
              testId="interview-traps"
            />
          </ResultSection>
        )}

        {(r.closing_questions_to_ask || []).length > 0 && (
          <ResultSection
            title="Ask Back"
            eyebrow="Your turn — close strong"
            count={(r.closing_questions_to_ask || []).length}
            accentGradient="linear-gradient(135deg,#10B981 0%,#047857 100%)"
            group="interview-result"
            testid="interview-section-askback"
          >
            <ReportList
              title="Your turn — close strong"
              color={RK.green}
              ordered
              items={r.closing_questions_to_ask}
              testId="interview-ask-back"
            />
          </ResultSection>
        )}
      </div>
    </div>
  );
}


export default function InterviewQuestions({ mode = "new" }) {
  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 pb-20">
        {mode === "new" && <InterviewWizard />}
        {mode === "result" && <InterviewResult />}
      </div>
    </div>
  );
}

export { InterviewWizard, InterviewResult };
